import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useGeoMarkupStore } from '@/store';
import { selectWorkspaceMapSettings } from '@/store/selectors';
import { getCurrentLanguage } from '@/config/i18n';
import {
  MAP_SETUP_DEFAULT_MAX_ZOOM,
  MAP_SETUP_DEFAULT_MIN_ZOOM,
  MAP_SETUP_DEFAULT_PL_COUNTRY_CODE,
  MAP_SETUP_DEFAULT_START_ZOOM,
  MAP_SETUP_ZOOM_MAX,
  MAP_SETUP_ZOOM_MIN,
} from '@/constants';
import {
  DEFAULT_BASEMAP_ID,
  getDefaultBasemapId,
  getVisibleBasemaps,
  resolveDefaultBasemap,
} from '@/config/maplibre';
import { capitalForCountry } from '@/utils/mapSettingsDefaults';
import type { BasemapId } from '@/config/maplibre';
import type { Coordinates, MapSettings } from '@/types';
import type { MapSetupValues } from '../types';

/** Clamp a value into the inclusive [min, max] range. */
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Allowlist default: every basemap allowed for the country (restricted ones gated out). */
function defaultAllowedBasemapIds(countryCode: string | null): BasemapId[] {
  return getVisibleBasemaps(null, countryCode).map((basemap) => basemap.id);
}

/**
 * Builds the initial form values for a configurator session.
 *
 * In edit mode (settings with a center) it mirrors the saved configuration,
 * filling basemap defaults for workspaces created before the basemap fields
 * existed. On first run it defaults Polish-language users to Poland (center =
 * Warsaw); other languages start with no country / no center.
 */
function buildInitialValues(settings: MapSettings | null): MapSetupValues {
  if (settings?.center) {
    const allowedBasemapIds =
      settings.allowedBasemapIds && settings.allowedBasemapIds.length > 0
        ? settings.allowedBasemapIds
        : defaultAllowedBasemapIds(settings.countryCode);
    return {
      center: { lat: settings.center.lat, lng: settings.center.lng },
      startZoom: settings.startZoom,
      minZoom: settings.minZoom,
      maxZoom: settings.maxZoom,
      countryCode: settings.countryCode,
      defaultBasemapId: getDefaultBasemapId(settings),
      allowedBasemapIds,
    };
  }

  const isPolish = getCurrentLanguage() === 'pl';
  const countryCode = isPolish ? MAP_SETUP_DEFAULT_PL_COUNTRY_CODE : null;

  return {
    center: capitalForCountry(countryCode),
    startZoom: MAP_SETUP_DEFAULT_START_ZOOM,
    minZoom: MAP_SETUP_DEFAULT_MIN_ZOOM,
    maxZoom: MAP_SETUP_DEFAULT_MAX_ZOOM,
    countryCode,
    defaultBasemapId: DEFAULT_BASEMAP_ID,
    allowedBasemapIds: defaultAllowedBasemapIds(countryCode),
  };
}

/** Public API of the configurator form hook. */
export interface UseMapSetupForm {
  /** Current form values. */
  values: MapSetupValues;
  /** Pick a country: updates the code, recenters, and re-gates the allowlist. */
  setCountry: (countryCode: string | null) => void;
  /** Set the center directly (e.g. from a preview marker drag). */
  setCenter: (coords: Coordinates) => void;
  /** Set the start zoom (clamped into [minZoom, maxZoom]). */
  setStartZoom: (value: number) => void;
  /** Lower the floor / raise dependents so start & max stay >= min. */
  setMinZoom: (value: number) => void;
  /** Raise the ceiling / lower dependents so start & min stay <= max. */
  setMaxZoom: (value: number) => void;
  /** Set the default basemap (auto-added to the allowlist when missing). */
  setDefaultBasemap: (id: BasemapId) => void;
  /** Replace the allowlist (kept non-empty; default re-pinned when dropped). */
  setAllowedBasemaps: (ids: BasemapId[]) => void;
}

/**
 * Local form state for the map configurator page with clamp-on-change zoom
 * coupling and basemap consistency (allowlist non-empty; default always within
 * the allowlist; PL-only orthophoto gated by the selected country).
 *
 * Initializes from the *target* workspace's saved settings (edit) or defaults
 * (first run). The target is the route-resolved `workspaceId`, not the active
 * one, so the form never picks up the previously-active workspace's settings
 * while `currentWorkspaceId` is still syncing. Re-initialization is keyed on
 * `workspaceId`, so switching workspaces reloads that workspace's settings while
 * in-workspace edits are preserved.
 */
export const useMapSetupForm = (
  workspaceId: string | null
): UseMapSetupForm => {
  const savedSettings = useGeoMarkupStore(
    selectWorkspaceMapSettings(workspaceId)
  );

  const [values, setValues] = useState<MapSetupValues>(() =>
    buildInitialValues(savedSettings)
  );

  // Re-initialize once per target workspace when its saved settings (with a
  // center) arrive — covers edit mode where the workspace list loads slightly
  // after the page, and switching to a different workspace.
  const initializedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (initializedForRef.current === workspaceId) return;
    if (savedSettings?.center) {
      setValues(buildInitialValues(savedSettings));
      initializedForRef.current = workspaceId;
    }
  }, [workspaceId, savedSettings]);

  const setCountry = useCallback((countryCode: string | null) => {
    const capital = capitalForCountry(countryCode);
    setValues((previous) => {
      // Drop allowlist entries no longer permitted for the selected country
      // (e.g. a country-restricted basemap when switching away from its country).
      const permittedIds = new Set(
        getVisibleBasemaps(null, countryCode).map((basemap) => basemap.id)
      );
      const filtered = previous.allowedBasemapIds.filter((id) =>
        permittedIds.has(id)
      );
      const allowedBasemapIds =
        filtered.length > 0 ? filtered : [DEFAULT_BASEMAP_ID];
      const defaultBasemapId = allowedBasemapIds.includes(
        previous.defaultBasemapId
      )
        ? previous.defaultBasemapId
        : DEFAULT_BASEMAP_ID;
      return {
        ...previous,
        countryCode,
        center: capital ?? previous.center,
        allowedBasemapIds,
        defaultBasemapId,
      };
    });
  }, []);

  const setCenter = useCallback((coords: Coordinates) => {
    setValues((previous) => ({ ...previous, center: coords }));
  }, []);

  const setStartZoom = useCallback((value: number) => {
    setValues((previous) => ({
      ...previous,
      startZoom: clamp(value, previous.minZoom, previous.maxZoom),
    }));
  }, []);

  const setMinZoom = useCallback((value: number) => {
    setValues((previous) => {
      const minZoom = clamp(value, MAP_SETUP_ZOOM_MIN, MAP_SETUP_ZOOM_MAX);
      const maxZoom = Math.max(previous.maxZoom, minZoom);
      const startZoom = clamp(previous.startZoom, minZoom, maxZoom);
      return { ...previous, minZoom, maxZoom, startZoom };
    });
  }, []);

  const setMaxZoom = useCallback((value: number) => {
    setValues((previous) => {
      const maxZoom = clamp(value, MAP_SETUP_ZOOM_MIN, MAP_SETUP_ZOOM_MAX);
      const minZoom = Math.min(previous.minZoom, maxZoom);
      const startZoom = clamp(previous.startZoom, minZoom, maxZoom);
      return { ...previous, minZoom, maxZoom, startZoom };
    });
  }, []);

  const setDefaultBasemap = useCallback((id: BasemapId) => {
    setValues((previous) => ({
      ...previous,
      defaultBasemapId: id,
      allowedBasemapIds: previous.allowedBasemapIds.includes(id)
        ? previous.allowedBasemapIds
        : [...previous.allowedBasemapIds, id],
    }));
  }, []);

  const setAllowedBasemaps = useCallback((ids: BasemapId[]) => {
    // An empty allowlist is allowed (the UI surfaces a "pick at least one"
    // message and blocks save); only the default is kept inside the list.
    setValues((previous) => ({
      ...previous,
      allowedBasemapIds: ids,
      defaultBasemapId: resolveDefaultBasemap(previous.defaultBasemapId, ids),
    }));
  }, []);

  return useMemo(
    () => ({
      values,
      setCountry,
      setCenter,
      setStartZoom,
      setMinZoom,
      setMaxZoom,
      setDefaultBasemap,
      setAllowedBasemaps,
    }),
    [
      values,
      setCountry,
      setCenter,
      setStartZoom,
      setMinZoom,
      setMaxZoom,
      setDefaultBasemap,
      setAllowedBasemaps,
    ]
  );
};
